import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useSubscriptionApi } from "src/_common/hooks/actions/subscription/appSubscriptionApiHook";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface PaymentModalProps {
  isVisible: boolean;
  onClose: () => void;
  planName: string;
  planPrice: string;
  planPeriod: string;
  onPaymentSuccess?: () => void;
  planId?: string;
  billingPeriod?: "monthly" | "yearly";
  aed_converted_price?: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isVisible,
  onClose,
  planName,
  planPrice,
  planPeriod,
  onPaymentSuccess,
  planId,
  billingPeriod = "monthly",
  aed_converted_price,
}) => {
  const toast = useToaster();
  const subscriptionApi = useSubscriptionApi();
  const { t } = useTranslation("payment");
  const authToken = useSelector((state: any) => state.user.token);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<"credit-card" | "paypal">("credit-card");
  const [selectedCard, setSelectedCard] = useState<string>("5361");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  // Compute display price based on selected payment method
  // const displayPrice = selectedPaymentMethod === 'paypal'
  //   ? `$${planPrice}`
  //   : `AED ${aed_converted_price || planPrice}`;
  const displayPrice = `$${planPrice}`;

  // console.log('Payment Method:', selectedPaymentMethod, 'Display Price:', displayPrice, 'AED Price:', aed_converted_price);

  // Mock payment cards
  const paymentCards = [
    {
      id: "5361",
      type: "mastercard",
      last4: "5361",
      expiry: "02/2028",
      isDefault: true,
    },
    {
      id: "1770",
      type: "visa",
      last4: "1770",
      expiry: "03/2030",
      isDefault: false,
    },
  ];

  const handlePayment = () => {
    if (!planId) {
      toast.error(t("paymentModal.validation.planMissing"));
      return;
    }

    if (selectedPaymentMethod === "paypal" || selectedPaymentMethod === "credit-card") {
      setIsProcessingPayment(true);

      // Call payment creation API
      subscriptionApi.callCreatePayment(
        {
          plan_id: planId,
          gateway_type: selectedPaymentMethod == "paypal" ? "P" : "T",
        },
        (message: string, response: any) => {
          console.log("Payment created successfully - Full response:", JSON.stringify(response, null, 2));
          setIsProcessingPayment(false);

          // Check multiple possible locations for approval_url in the response
          const approvalUrl =
            response?.approval_url ||
            response?.data?.approval_url ||
            response?.data?.data?.approval_url ||
            response?.result?.approval_url;

          console.log("Extracted approval URL:", approvalUrl);

          if (approvalUrl) {
            console.log("Redirecting to payment gateway:", approvalUrl);

            // Verify token exists before redirecting
            if (!authToken) {
              toast.error(t("paymentModal.validation.authError"));
              setIsProcessingPayment(false);
              return;
            }

            toast.success(t("paymentModal.validation.redirecting"));

            // Close modal before redirecting
            onClose();

            // Redirect in the same tab
            window.location.href = approvalUrl;
          } else {
            console.error("Approval URL not found in response. Response structure:", response);
            toast.error(t("paymentModal.validation.urlNotReceived"));
          }
        },
        (error: any) => {
          console.error("Payment creation failed:", error);
          setIsProcessingPayment(false);
          toast.error(error?.message || t("paymentModal.validation.paymentFailed"));
        },
      );
    } else {
      return;
    }
  };

  if (!isVisible) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="payment-modal-header">
          <button className="payment-back-button" onClick={onClose}>
            ← {t("paymentModal.header.back")}
          </button>
          <h2 className="payment-modal-title">{t("paymentModal.header.title")}</h2>
          <button className="payment-close-button" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Plan Summary */}
        <div className="payment-plan-summary">
          <div className="plan-access-duration">
            {planName} {t("paymentModal.planSummary.plan")}{" "}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="payment-summary">
          <div className="payment-line-item">
            <span>
              {planName} {t("paymentModal.summary.plan")} {planPeriod}
            </span>
            <span className="payment-amount">{displayPrice}</span>
          </div>
          <div className="payment-total">
            <span>{t("paymentModal.summary.totalAmount")}</span>
            <span className="payment-total-amount">{displayPrice}</span>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="payment-methods-section">
          {/* Credit Card Option */}
          <div className={`payment-method-option ${selectedPaymentMethod === "credit-card" ? "selected" : ""}`}>
            <div className="payment-method-header">
              <input
                type="radio"
                id="credit-card"
                name="payment-method"
                checked={selectedPaymentMethod === "credit-card"}
                onChange={() => setSelectedPaymentMethod("credit-card")}
              />
              <label htmlFor="credit-card" className="payment-method-label">
                {t("paymentModal.paymentMethods.creditCard")}
              </label>
              <div className="payment-method-icons">
                <img src="/images/mastercard-icon.png" alt="Mastercard" className="payment-icon" />
                <img src="/images/visa-icon.png" alt="Visa" className="payment-icon" />
              </div>
            </div>

            {/* Credit Cards List */}
            {selectedPaymentMethod === "credit-card" && (
              <div className="credit-cards-list d-none">
                {paymentCards.map((card) => (
                  <div
                    key={card.id}
                    className={`credit-card-item ${selectedCard === card.id ? "selected" : ""}`}
                    onClick={() => setSelectedCard(card.id)}
                  >
                    <div className="card-info">
                      <div className="card-brand-icon">
                        {card.type === "mastercard" ? (
                          <div className="mastercard-icon">
                            <div className="mc-circle red"></div>
                            <div className="mc-circle yellow"></div>
                          </div>
                        ) : (
                          <div className="visa-icon">VISA</div>
                        )}
                      </div>
                      <div className="card-details">
                        <div className="card-number">•••• {card.last4}</div>
                        <div className="card-expiry">
                          {t("paymentModal.paymentMethods.expires")} {card.expiry}
                        </div>
                      </div>
                      {card.isDefault && (
                        <div className="default-badge">{t("paymentModal.paymentMethods.default")}</div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add New Card Button */}
                <button className="add-new-card-button">{t("paymentModal.paymentMethods.addNewCard")}</button>
              </div>
            )}
          </div>

          {/* PayPal Option */}
          <div className={`payment-method-option ${selectedPaymentMethod === "paypal" ? "selected" : ""}`}>
            <div className="payment-method-header">
              <input
                type="radio"
                id="paypal"
                name="payment-method"
                checked={selectedPaymentMethod === "paypal"}
                onChange={() => setSelectedPaymentMethod("paypal")}
              />
              <label htmlFor="paypal" className="payment-method-label">
                {t("paymentModal.paymentMethods.paypal")}
              </label>
              <div className="paypal-icon">
                <img src="/images/paypal-icon.png" alt="PayPal" className="payment-icon" />
              </div>
            </div>
          </div>
        </div>

        {/* Pay Button */}
        <div className="payment-actions">
          <button className="pay-button" onClick={handlePayment} disabled={isProcessingPayment}>
            {isProcessingPayment
              ? t("paymentModal.buttons.processing")
              : `${t("paymentModal.buttons.pay")} ${displayPrice}`}
          </button>
        </div>

        {/* Security Notice */}
        <div className="payment-security">
          <div className="security-icon">🔒</div>
          <span>{t("paymentModal.security.message")}</span>
        </div>

        {/* Terms Notice */}
        <div className="payment-terms">
          <p>
            {t("paymentModal.terms.message")}{" "}
            <a href="/cms/terms-and-conditions" target="_blank" className="terms-link">
              {t("paymentModal.terms.termsOfUse")}
            </a>{" "}
            {t("paymentModal.terms.and")}{" "}
            <a href="/cms/privacy-policy" target="_blank" className="terms-link">
              {t("paymentModal.terms.privacy")}
            </a>
            . {t("paymentModal.terms.chargeInfo")} {displayPrice} {planPeriod} {t("paymentModal.terms.until")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
